import { _ as _define_property } from "@swc/helpers/_/_define_property";
import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { format as formatDate } from 'date-fns';
import Decimal from 'decimal.js';
import * as serviceCostModel from '~/models/service-cost/pricing';
import { makeDateStampObject } from './time/makers';
import { fromDateStampObject } from './time/translator';
export var StagesValueType = /*#__PURE__*/ function(StagesValueType) {
    StagesValueType["Percent"] = "percent";
    StagesValueType["Amount"] = "amount";
    return StagesValueType;
}({});
export var StagesTerms = /*#__PURE__*/ function(StagesTerms) {
    StagesTerms["AutoFirst"] = "auto_first";
    StagesTerms["AllManual"] = "all_manual";
    return StagesTerms;
}({});
export var BillingStageScheduleType = /*#__PURE__*/ function(BillingStageScheduleType) {
    BillingStageScheduleType["OnEffectiveDate"] = "onEffectiveDate";
    BillingStageScheduleType["RelativeToEffectiveDate"] = "relativeToEffectiveDate";
    BillingStageScheduleType["RelativeToAcceptance"] = "relativeToAcceptance";
    BillingStageScheduleType["OnDate"] = "onDate";
    BillingStageScheduleType["Manual"] = "manual";
    BillingStageScheduleType["RequiresDateSelection"] = "requiresDateSelection";
    return BillingStageScheduleType;
}({});
export class StageScheduleModel {
    match(visitor) {
        const { schedule } = this;
        switch(schedule.type){
            case "onEffectiveDate":
                return visitor.onEffectiveDate(schedule);
            case "relativeToEffectiveDate":
                return visitor.relativeToEffectiveDate(schedule);
            case "relativeToAcceptance":
                return visitor.relativeToAcceptance(schedule);
            case "onDate":
                return visitor.onDate(schedule);
            case "manual":
                return visitor.manual(schedule);
            case "requiresDateSelection":
                return visitor.requiresDateSelection(schedule);
        }
    }
    constructor(schedule){
        _define_property(this, "schedule", void 0);
        this.schedule = schedule;
    }
}
export var StageScheduleContext = /*#__PURE__*/ function(StageScheduleContext) {
    StageScheduleContext["ProposalOnAcceptance"] = "proposal-on-acceptance";
    StageScheduleContext["ProposalOnEffectiveDate"] = "proposal-on-effective-date";
    StageScheduleContext["Amendment"] = "amendment";
    StageScheduleContext["Template"] = "template";
    return StageScheduleContext;
}({});
export var StageScheduleValidationError = /*#__PURE__*/ function(StageScheduleValidationError) {
    StageScheduleValidationError["SetDate"] = "setDate";
    StageScheduleValidationError["DateBeforeEffectiveDate"] = "dateBeforeEffectiveDate";
    StageScheduleValidationError["DateMustBeFuture"] = "dateMustBeFuture";
    StageScheduleValidationError["DateMustBeFutureOnAcceptance"] = "dateMustBeFutureOnAcceptance";
    StageScheduleValidationError["DaysRequired"] = "daysRequired";
    StageScheduleValidationError["DaysAtLeastOne"] = "daysAtLeastOne";
    return StageScheduleValidationError;
}({});
const scheduleFromDTO = (dto)=>{
    if (!dto) return undefined;
    switch(dto.type){
        case "onEffectiveDate":
            return {
                type: "onEffectiveDate"
            };
        case "manual":
            return {
                type: "manual"
            };
        case "requiresDateSelection":
            return {
                type: "requiresDateSelection"
            };
        case "relativeToEffectiveDate":
            var _dto_relativeToEffectiveDate;
            return {
                type: "relativeToEffectiveDate",
                relativeToEffectiveDate: (_dto_relativeToEffectiveDate = dto.relativeToEffectiveDate) !== null && _dto_relativeToEffectiveDate !== void 0 ? _dto_relativeToEffectiveDate : {
                    days: 0
                }
            };
        case "relativeToAcceptance":
            var _dto_relativeToAcceptance;
            return {
                type: "relativeToAcceptance",
                relativeToAcceptance: (_dto_relativeToAcceptance = dto.relativeToAcceptance) !== null && _dto_relativeToAcceptance !== void 0 ? _dto_relativeToAcceptance : {
                    days: 0
                }
            };
        case "onDate":
            return {
                type: "onDate",
                onDate: {
                    date: dto.onDate ? formatDate(fromDateStampObject(dto.onDate.date), 'yyyy-MM-dd') : ''
                }
            };
    }
};
const notRelative = ()=>null;
export const relativeScheduleTypeOf = (schedule)=>new StageScheduleModel(schedule).match({
        onEffectiveDate: notRelative,
        relativeToEffectiveDate: (relative)=>relative.type,
        relativeToAcceptance: (relative)=>relative.type,
        onDate: notRelative,
        manual: notRelative,
        requiresDateSelection: notRelative
    });
const noRelativeDays = ()=>0;
export const relativeDaysOf = (schedule)=>new StageScheduleModel(schedule).match({
        onEffectiveDate: noRelativeDays,
        relativeToEffectiveDate: (relative)=>relative.relativeToEffectiveDate.days,
        relativeToAcceptance: (relative)=>relative.relativeToAcceptance.days,
        onDate: noRelativeDays,
        manual: noRelativeDays,
        requiresDateSelection: noRelativeDays
    });
export const relativeScheduleWithDays = (type, days)=>type === "relativeToEffectiveDate" ? {
        type,
        relativeToEffectiveDate: {
            days
        }
    } : {
        type,
        relativeToAcceptance: {
            days
        }
    };
const stageScheduleAnchoredOn = (schedule, anchor)=>{
    if (!schedule) return schedule;
    const currentAnchor = relativeScheduleTypeOf(schedule);
    if (currentAnchor == null || currentAnchor === anchor) return schedule;
    return relativeScheduleWithDays(anchor, relativeDaysOf(schedule));
};
// WHY: a solo stage's auto-computed value is always exactly 100%, so only the last stage is allowed to hit it.
const exceedsPercentBound = (value, isLast)=>isLast ? value.gt(100) : value.gte(100);
const validateRelativeDays = (days)=>{
    if (days <= 0) return "daysAtLeastOne";
    return null;
};
const validateOnDate = (params)=>{
    const { date, context, effectiveDate, today } = params;
    // WHY: a template has no proposal date, so a past stage date is checked only once it joins a proposal (ANCR-3345).
    if (context === "template") return null;
    if (!date) return "setDate";
    switch(context){
        case "proposal-on-effective-date":
            if (effectiveDate && date < effectiveDate) return "dateBeforeEffectiveDate";
            return null;
        case "amendment":
            if (date === today) return null;
            if (effectiveDate && effectiveDate > today && date < effectiveDate) return "dateBeforeEffectiveDate";
            if (date < today) return "dateMustBeFuture";
            return null;
        case "proposal-on-acceptance":
            if (effectiveDate) {
                return date < effectiveDate ? "dateBeforeEffectiveDate" : null;
            }
            return date <= today ? "dateMustBeFutureOnAcceptance" : null;
    }
};
const anchoredOnAgreementStart = ()=>true;
const notAnchoredOnAgreementStart = ()=>false;
const isAnchoredOnAgreementStart = (schedule)=>new StageScheduleModel(schedule).match({
        onEffectiveDate: anchoredOnAgreementStart,
        relativeToEffectiveDate: anchoredOnAgreementStart,
        relativeToAcceptance: anchoredOnAgreementStart,
        onDate: notAnchoredOnAgreementStart,
        manual: notAnchoredOnAgreementStart,
        requiresDateSelection: notAnchoredOnAgreementStart
    });
const termsStageSchedule = (terms, stageNumber)=>terms === "auto_first" && stageNumber === 1 ? {
        type: "onEffectiveDate"
    } : {
        type: "manual"
    };
const typeOnlyDTO = (schedule)=>({
        type: schedule.type
    });
const onDateToDTO = (schedule)=>{
    if (!schedule.onDate.date) return {
        type: "requiresDateSelection"
    };
    const [yearStr, monthStr, dayStr] = schedule.onDate.date.split('-');
    return {
        type: schedule.type,
        onDate: {
            date: makeDateStampObject(parseInt(yearStr, 10), parseInt(monthStr, 10), parseInt(dayStr, 10))
        }
    };
};
const scheduleToDTO = (schedule)=>new StageScheduleModel(schedule).match({
        onEffectiveDate: typeOnlyDTO,
        relativeToEffectiveDate: (relative)=>({
                type: relative.type,
                relativeToEffectiveDate: relative.relativeToEffectiveDate
            }),
        relativeToAcceptance: (relative)=>({
                type: relative.type,
                relativeToAcceptance: relative.relativeToAcceptance
            }),
        onDate: onDateToDTO,
        manual: typeOnlyDTO,
        requiresDateSelection: typeOnlyDTO
    });
export class OneTimeInStagesModel {
    static fromDTO(dto) {
        return new OneTimeInStagesModel({
            valueType: dto.valueType,
            terms: dto.terms,
            stages: dto.stages.map((stage)=>({
                    stageNumber: stage.stageNumber,
                    value: new Decimal(stage.value).div(100),
                    stageSchedule: scheduleFromDTO(stage.stageSchedule)
                }))
        });
    }
    static defaultStageSchedule() {
        return {
            type: "manual"
        };
    }
    static isStageScheduleValid(params) {
        const { schedule, context, effectiveDate, today } = params;
        if (!schedule) return null;
        const alwaysValid = ()=>null;
        return new StageScheduleModel(schedule).match({
            onEffectiveDate: alwaysValid,
            relativeToEffectiveDate: (relative)=>validateRelativeDays(relative.relativeToEffectiveDate.days),
            relativeToAcceptance: (relative)=>validateRelativeDays(relative.relativeToAcceptance.days),
            onDate: (onDate)=>validateOnDate({
                    date: onDate.onDate.date,
                    context,
                    effectiveDate,
                    today
                }),
            manual: alwaysValid,
            requiresDateSelection: ()=>context === "template" ? null : "setDate"
        });
    }
    static forSaveAsTemplate(params) {
        const { stages, today } = params;
        return stages.map((stage)=>{
            const stageSchedule = stage.stageSchedule;
            if (stageSchedule && stageSchedule.type === "onDate" && (!stageSchedule.onDate.date || stageSchedule.onDate.date < today)) {
                return _object_spread_props(_object_spread({}, stage), {
                    stageSchedule: {
                        type: "requiresDateSelection"
                    }
                });
            }
            return stage;
        });
    }
    static make(data) {
        return new OneTimeInStagesModel(data);
    }
    static makeStage(stageNumber, value, stageSchedule) {
        return {
            stageNumber,
            value,
            stageSchedule
        };
    }
    static isEffectiveDateInFuture(effectiveDate, today) {
        return effectiveDate != null && !!today && effectiveDate > today;
    }
    toDTO() {
        return {
            valueType: this.data.valueType,
            terms: this.data.terms,
            stages: this.scheduledStages().map((stage)=>({
                    stageNumber: stage.stageNumber,
                    value: stage.value.mul(100).toNumber(),
                    stageSchedule: scheduleToDTO(stage.stageSchedule)
                }))
        };
    }
    scheduledStages() {
        const { terms } = this.data;
        return this.data.stages.map((stage)=>{
            var _stage_stageSchedule;
            return _object_spread_props(_object_spread({}, stage), {
                stageSchedule: (_stage_stageSchedule = stage.stageSchedule) !== null && _stage_stageSchedule !== void 0 ? _stage_stageSchedule : termsStageSchedule(terms, stage.stageNumber)
            });
        });
    }
    withStageSchedule(index, stageSchedule) {
        const stages = this.data.stages.map((stage, i)=>i === index ? _object_spread_props(_object_spread({}, stage), {
                stageSchedule
            }) : stage);
        return new OneTimeInStagesModel(_object_spread_props(_object_spread({}, this.data), {
            stages
        }));
    }
    withAnchor(anchor) {
        const stages = this.data.stages.map((stage)=>_object_spread_props(_object_spread({}, stage), {
                stageSchedule: stageScheduleAnchoredOn(stage.stageSchedule, anchor)
            }));
        return new OneTimeInStagesModel(_object_spread_props(_object_spread({}, this.data), {
            stages
        }));
    }
    forAmendment(params) {
        const { effectiveDate, today } = params;
        if (OneTimeInStagesModel.isEffectiveDateInFuture(effectiveDate, today)) return this;
        const todaySchedule = {
            type: "onDate",
            onDate: {
                date: today
            }
        };
        const stages = this.data.stages.map((stage)=>stage.stageSchedule != null && isAnchoredOnAgreementStart(stage.stageSchedule) ? _object_spread_props(_object_spread({}, stage), {
                stageSchedule: todaySchedule
            }) : stage);
        return new OneTimeInStagesModel(_object_spread_props(_object_spread({}, this.data), {
            stages
        }));
    }
    numStages() {
        return this.data.stages.length;
    }
    hasInvalidStages(params) {
        const stages = this.data.stages;
        if (stages.length === 0) return false;
        const valueType = this.data.valueType;
        const isPercentage = valueType === "percent";
        const lastIndex = stages.length - 1;
        if (stages.some((s, i)=>s.value.lte(0) || isPercentage && exceedsPercentBound(s.value, i === lastIndex))) return true;
        const cost = params === null || params === void 0 ? void 0 : params.cost;
        if (cost) {
            const totalRate = serviceCostModel.price(cost);
            const isValueTypeDollars = valueType === "amount";
            const totalSum = stages.reduce((acc, stage)=>acc.plus(stage.value), new Decimal(0));
            // Amount stages must sum exactly to the price; the backend rejects any mismatch with total_amount_mismatch.
            if (isValueTypeDollars && !totalSum.eq(totalRate)) return true;
            const totalSumExceedsRate = totalSum.gt(isValueTypeDollars ? totalRate : new Decimal(100));
            const valueLevelInvalid = stages.some((stage, index)=>{
                const isLast = index === stages.length - 1;
                if (valueType === "percent" && exceedsPercentBound(stage.value, isLast)) return true;
                if (isValueTypeDollars && stage.value.gt(totalRate)) return true;
                if (valueType === "percent" && !isLast && totalSumExceedsRate) return true;
                if (isValueTypeDollars && !isLast && totalSumExceedsRate) return true;
                if (isLast && stage.value.eq(0)) return true;
                if (stage.value.eq(0)) return true;
                return false;
            });
            if (valueLevelInvalid) return true;
        }
        const scheduleContext = params === null || params === void 0 ? void 0 : params.scheduleContext;
        if (scheduleContext) {
            return stages.some((stage)=>OneTimeInStagesModel.isStageScheduleValid({
                    schedule: stage.stageSchedule,
                    context: scheduleContext.context,
                    effectiveDate: scheduleContext.effectiveDate,
                    today: scheduleContext.today
                }) !== null);
        }
        return false;
    }
    stagesWithFinalAmount(cost) {
        const discountedPrice = serviceCostModel.applyDiscount(cost);
        const price = serviceCostModel.price(cost);
        const isPercentage = this.data.valueType === "percent";
        const stages = this.data.stages.map((stage)=>_object_spread_props(_object_spread({}, stage), {
                discountedFinalAmount: isPercentage ? discountedPrice.mul(stage.value).div(100).toDP(2) : stage.value.mul(discountedPrice).div(price).toDP(2),
                finalAmount: isPercentage ? price.mul(stage.value).div(100).toDP(2) : stage.value
            }));
        if (isPercentage) {
            stages[stages.length - 1].discountedFinalAmount = stages.slice(0, -1).reduce((acc, curr)=>acc.minus(curr.discountedFinalAmount), discountedPrice);
            stages[stages.length - 1].finalAmount = stages.slice(0, -1).reduce((acc, curr)=>acc.minus(curr.finalAmount), price);
        }
        return stages;
    }
    terms() {
        return this.data.terms;
    }
    valueType() {
        return this.data.valueType;
    }
    isAutoFirst() {
        return this.data.terms === "auto_first";
    }
    stages() {
        return this.data.stages;
    }
    setTerms(terms) {
        return new OneTimeInStagesModel(_object_spread_props(_object_spread({}, this.data), {
            terms
        }));
    }
    setStages(stages) {
        return new OneTimeInStagesModel(_object_spread_props(_object_spread({}, this.data), {
            stages
        }));
    }
    setValueType(valueType) {
        return new OneTimeInStagesModel(_object_spread_props(_object_spread({}, this.data), {
            valueType
        }));
    }
    applyValueTypeChange(change) {
        return new OneTimeInStagesModel(_object_spread_props(_object_spread({}, this.data), {
            valueType: change.valueType,
            stages: change.stages
        }));
    }
    constructor(data){
        _define_property(this, "data", void 0);
        this.data = data;
    }
}
